export interface StoredClientEvent { id: string; at: string; sessionId: string; buildId: string; type: string; [key: string]: unknown }
const MAX_BYTES = 4 * 1024 ** 2;
const MAX_AGE = 14 * 86400_000;
const size = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

/** The API's per-field string limits (clientEventsSchema in src/server/app.ts); an event over one is refused for good. */
const FIELD_LIMITS: Record<string, number> = { id: 100, sessionId: 100, buildId: 100, type: 40, at: 40, message: 2000, planId: 64, tripId: 64 };

function capFields(event: StoredClientEvent): StoredClientEvent {
  const capped: StoredClientEvent = { ...event };
  for (const [field, limit] of Object.entries(FIELD_LIMITS)) {
    const text = capped[field];
    if (typeof text === 'string' && text.length > limit) capped[field] = text.slice(0, limit);
  }
  return capped;
}

/** Normalize once, before persistence, so a retry cannot reuse an ID with different data. */
export function normalizeEvent(value: StoredClientEvent): StoredClientEvent {
  try {
    const seen = new WeakSet<object>();
    const text = JSON.stringify(value, (_key, item: unknown) => {
      if (typeof item === 'string') return item.slice(0, 4000);
      if (typeof item === 'number' && !Number.isFinite(item)) return String(item);
      if (typeof item === 'bigint') return String(item);
      if (item && typeof item === 'object') { if (seen.has(item)) return '[circular]'; seen.add(item); }
      return item;
    });
    const event = capFields(JSON.parse(text) as StoredClientEvent);
    if (size(event) <= 16 * 1024) return event;
    return capFields({ id: value.id, at: value.at, sessionId: value.sessionId, buildId: value.buildId, type: value.type, message: 'Event details exceeded 16 KiB and were omitted.', truncated: true });
  } catch {
    return capFields({ id: value.id, at: value.at, sessionId: value.sessionId, buildId: value.buildId, type: value.type, message: 'Event details could not be normalized.', truncated: true });
  }
}

export class TelemetryOutbox {
  private database?: Promise<IDBDatabase>;
  private memory = new Map<string, StoredClientEvent>();
  private memoryOnly = false;
  dropped = 0;
  get durable() { return !this.memoryOnly; }

  constructor(private readonly factory: IDBFactory | undefined = typeof indexedDB === 'undefined' ? undefined : indexedDB) {}

  private db(): Promise<IDBDatabase> {
    if (!this.factory) return Promise.reject(new Error('Browser storage unavailable'));
    return this.database ??= new Promise((resolve, reject) => {
      const request = this.factory!.open('ridge-and-road:diagnostics:v1', 1);
      request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains('events')) request.result.createObjectStore('events', { keyPath: 'id' }); };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Diagnostic database upgrade blocked'));
    });
  }

  private async transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore, finish: (value: T) => void) => void): Promise<T> {
    const database = await this.db();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction('events', mode);
      let value: T;
      transaction.oncomplete = () => resolve(value);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ?? new Error('Diagnostic storage transaction aborted'));
      action(transaction.objectStore('events'), (result) => { value = result; });
    });
  }

  private trim(events: StoredClientEvent[]): StoredClientEvent[] {
    const ordered = events.sort((a, b) => a.at.localeCompare(b.at));
    let bytes = ordered.reduce((n, event) => n + size(event), 0);
    while (ordered.length && (ordered.length > 500 || bytes > MAX_BYTES || Date.now() - Date.parse(ordered[0]!.at) > MAX_AGE)) { bytes -= size(ordered.shift()); this.dropped++; }
    return ordered;
  }

  async put(event: StoredClientEvent): Promise<void> {
    this.memory.set(event.id, event);
    if (!this.memoryOnly) {
      try {
        await this.transaction<void>('readwrite', (store, finish) => {
          store.put(event);
          const request = store.getAll();
          request.onsuccess = () => {
            const all = request.result as StoredClientEvent[];
            const retained = new Set(this.trim(all.slice()).map((item) => item.id));
            for (const item of all) if (!retained.has(item.id)) store.delete(item.id);
            finish(undefined);
          };
        });
        this.memory.delete(event.id);
        return;
      } catch { this.memoryOnly = true; }
    }
    this.memory = new Map(this.trim([...this.memory.values()]).map((item) => [item.id, item]));
  }

  async pending(): Promise<StoredClientEvent[]> {
    if (!this.memoryOnly) {
      try {
        const events = await this.transaction<StoredClientEvent[]>('readwrite', (store, finish) => {
          const request = store.getAll();
          request.onsuccess = () => {
            const all = request.result as StoredClientEvent[];
            const retained = this.trim(all.slice());
            const ids = new Set(retained.map((event) => event.id));
            for (const event of all) if (!ids.has(event.id)) store.delete(event.id);
            finish(retained);
          };
        });
        return [...new Map([...events, ...this.memory.values()].map((event) => [event.id, event])).values()];
      } catch { this.memoryOnly = true; }
    }
    return this.trim([...this.memory.values()]);
  }

  /** Remove events the API will never accept; they are counted as dropped, not delivered. */
  async reject(ids: string[]): Promise<void> {
    this.dropped += ids.length;
    await this.acknowledge(ids);
  }

  async acknowledge(ids: string[]): Promise<void> {
    if (!this.memoryOnly) {
      try { await this.transaction<void>('readwrite', (store, finish) => { for (const id of ids) store.delete(id); finish(undefined); }); }
      catch { this.memoryOnly = true; }
    }
    for (const id of ids) this.memory.delete(id);
  }
}
